import { BaseRemoteService } from "@ofqual/portal-core/core/dataAccess";
import {
  PagedResponse,
  QueryParams,
  Opts,
  Model,
  ValidatedResponse,
  JSONPatchDocument,
} from "@ofqual/portal-core/core/types";
import { paramsToSearchString } from "@ofqual/portal-core/core/utils/urlHelper";
import { getValidationError } from "@ofqual/portal-core/core/utils/validationHelper";
import { PagedResponseTo } from "@ofqual/portal-core/core/utils/pagedResponseHelpers";

import { toUser, User } from "../domain/User";

export class UsersService extends BaseRemoteService {
  public async search(
    params: QueryParams,
    opts: Opts
  ): Promise<PagedResponse<User>> {
    const url = `${this.apiRoot}/users/${paramsToSearchString(params)}`;
    const response = await super.getRequest<PagedResponse<Model>>(url, opts);

    return PagedResponseTo(response, toUser);
  }

  public async get(upn: string, opts: Opts): Promise<User> {
    const url = `${this.apiRoot}/users/${upn}?logResourceVisit=true`;
    const response = await super.getRequest<Model>(url, opts);

    return toUser(response);
  }

  public async createUser(
    user: User,
    opts: Opts
  ): Promise<ValidatedResponse<User>> {
    const url = `${this.apiRoot}/users/`;
    try {
      const response = await super.postRequest<Model>(url, user, opts);

      return {
        isValid: true,
        data: toUser(response),
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async updateUser(
    upn: string,
    patchDocument: JSONPatchDocument,
    opts: Opts
  ): Promise<ValidatedResponse<Model>> {
    const url = `${this.apiRoot}/users/${upn}`;
    try {
      const response = await this.patchRequest<User>(url, patchDocument, opts);
      return {
        isValid: true,
        data: toUser(response),
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async activateUser(
    upn: string,
    active: boolean,
    opts: Opts
  ): Promise<User> {
    const url = `${this.apiRoot}/users/${upn}/${
      active ? "deactivate" : "reactivate"
    }`;
    const response = await super.postRequest<User>(url, null, opts);

    return toUser(response);
  }

  public async resetPassword(upn: string, opts: Opts): Promise<User> {
    const url = `${this.apiRoot}/users/${upn}/resetpassword`;
    const response = await super.postRequest<User>(url, null, opts);

    return toUser(response);
  }
}
